import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ImageIcon } from 'lucide-react';
import { Logo } from '../components/Logo';
import { LogoApple } from '../components/AccesoSocial';
import { NOMBRE_APP, LEMA_APP, URL_APP_STORE } from '../config/marca';

/**
 * Página de presentación de la web para quien no ha iniciado sesión (ruta /).
 * En la app de iOS no sale: allí se va directo a la pantalla de entrada (por eso la
 * insignia del App Store solo se ve en la web).
 * Los textos salen de lo que cuenta la ayuda (src/config/ayuda.ts): si una
 * función cambia allí, conviene repasar también esto.
 *
 * Las imágenes viven en public/bienvenida/ y salen de las maquetas de
 * scripts/bienvenida/escenas.html con `npm run bienvenida`. Si falta alguna, en su hueco
 * sale un recuadro con el nombre del archivo que se espera y qué debe mostrar.
 */

/** Imagen de una sección; si el archivo aún no existe, un hueco que lo dice. */
const Imagen: React.FC<{ archivo: string; alt: string; idea: string; className?: string }> =
  ({ archivo, alt, idea, className = '' }) => {
    const [falta, setFalta] = useState(false);
    if (falta) {
      return (
        <div className={`aspect-[4/3] w-full rounded-3xl border-2 border-dashed border-gray-300 dark:border-gray-700
                         flex flex-col items-center justify-center text-center p-6 text-gray-400 ${className}`}>
          <ImageIcon className="w-8 h-8 mb-2" />
          <p className="font-mono text-sm">public/bienvenida/{archivo}</p>
          <p className="text-xs mt-1 max-w-xs">{idea}</p>
        </div>
      );
    }
    return (
      <img src={`/bienvenida/${archivo}`} alt={alt} loading="lazy" onError={() => setFalta(true)}
        className={`w-full h-auto rounded-3xl ${className}`} />
    );
  };

const BotonCrear: React.FC<{ grande?: boolean }> = ({ grande }) => (
  <Link to="/signup"
    className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold text-white
                bg-blue-600 hover:bg-blue-700 transition ${grande ? 'px-7 py-3.5 text-base' : 'px-4 py-2 text-sm'}`}>
    Crear cuenta <ArrowRight className="w-4 h-4" />
  </Link>
);

/** Insignia oficial de Apple (Apple Marketing Tools), servida por Apple tal cual: negra en claro, blanca en oscuro. */
const insigniaAppStore = (color: 'black' | 'white') =>
  `https://toolbox.marketingtools.apple.com/api/v2/badges/download-on-the-app-store/${color}/es-es?releaseDate=1306195200`;

/**
 * Insignia «Descargar en el App Store». Usa la imagen oficial de Apple; si no carga (sin
 * conexión con Apple, un bloqueador…), se dibuja una igual (fondo negro, borde gris, manzana y
 * texto en blanco). Apple pide no cambiarle colores ni proporciones, que mida al menos 40 px
 * de alto y que tenga aire alrededor.
 */
const InsigniaAppStore: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [sinImagen, setSinImagen] = useState(false);
  return (
    <a href={URL_APP_STORE} target="_blank" rel="noopener noreferrer"
      aria-label={`Descargar ${NOMBRE_APP} en el App Store`}
      className={`inline-flex hover:opacity-90 transition ${className}`}>
      {sinImagen ? (
        <span className="inline-flex items-center gap-2 h-12 pl-3 pr-4 rounded-[10px] bg-black text-white border border-[#A6A6A6]">
          <LogoApple className="h-7 w-auto" />
          <span className="flex flex-col items-start leading-none text-left"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif' }}>
            <span className="text-[10px] tracking-wide">Descargar en el</span>
            <span className="text-[22px] font-semibold -tracking-[0.02em] mt-0.5">App Store</span>
          </span>
        </span>
      ) : (
        <>
          <img src={insigniaAppStore('black')} alt="Descargar en el App Store" className="h-12 w-auto dark:hidden"
            onError={() => setSinImagen(true)} />
          <img src={insigniaAppStore('white')} alt="Descargar en el App Store" className="h-12 w-auto hidden dark:block"
            onError={() => setSinImagen(true)} />
        </>
      )}
    </a>
  );
};

const SECCIONES = [
  {
    etiqueta: 'Finanzas',
    color: 'text-blue-600 dark:text-blue-400',
    titulo: 'Sabe en qué se va tu dinero',
    texto: 'Tus cuentas, gastos e ingresos en un vistazo. Pon un presupuesto a cada categoría, deja que los movimientos de cada mes se apunten solos y mira en los informes cómo vas.',
    archivo: 'finanzas.webp',
    alt: 'Resumen de Finanzas en myarinn',
    idea: 'Finanzas: el resumen de cuentas y presupuestos en el móvil.',
  },
  {
    etiqueta: 'Informes',
    color: 'text-lumbre-600 dark:text-lumbre-400',
    titulo: 'Tu mes, contado con palabras',
    texto: 'Además de gráficos, los informes te explican qué ha pasado: cuánto ahorraste, qué se disparó frente a lo habitual, cómo acabarán tus presupuestos a este ritmo y qué días gastas más.',
    archivo: 'informes.webp',
    alt: 'Informes de myarinn con el resumen del mes',
    idea: 'Informes: el resumen del mes explicado con texto y la evolución.',
  },
  {
    etiqueta: 'A medias',
    color: 'text-emerald-600 dark:text-emerald-400',
    titulo: 'Viajes y cenas sin echar cuentas',
    texto: 'Apunta quién pagó qué y la app calcula la forma más corta de quedar en paz. Guarda la foto del ticket, recuerda los pagos por WhatsApp y descarga el resumen en PDF.',
    archivo: 'amedias.webp',
    alt: 'Un plan de gastos compartidos en myarinn',
    idea: 'A medias: un viaje con amigos y quién debe a quién.',
  },
  {
    etiqueta: 'Tareas',
    color: 'text-orange-600 dark:text-orange-400',
    titulo: 'Las tareas de casa, repartidas',
    texto: 'Apunta qué toca y cada cuánto, y repártelo entre quienes vivís juntos. Todos veis la misma semana, cada uno con su color, y el iPhone te avisa de lo que te toca. Los peques no necesitan cuenta.',
    archivo: 'tareas.webp',
    alt: 'La semana de tareas de una casa compartida en myarinn',
    idea: 'Tareas: la semana de una casa, con quién hace cada tarea.',
  },
  {
    etiqueta: 'Préstamos',
    color: 'text-cyan-600 dark:text-cyan-400',
    titulo: 'Lo que prestas, vuelve',
    texto: 'El taladro, un libro, la tienda de campaña… Anota qué has dejado y a quién, y no vuelvas a preguntarte dónde acabó.',
    archivo: 'prestamos.webp',
    alt: 'Lista de cosas prestadas en myarinn',
    idea: 'Préstamos: objetos prestados a amigos.',
  },
];

const VENTAJAS = [
  ['Se apunta al pagar', 'Paga con Apple Pay y myarinn se abre con el gasto, la tienda y la cuenta de tu tarjeta. O díselo a Siri.'],
  ['Todo conectado', 'Tu parte de un plan de A medias puede pasar sola a tus Finanzas.'],
  ['Web y iPhone', 'La misma cuenta en el navegador y en la app, que funciona sin conexión.'],
  ['Privado', 'Datos en servidores europeos y bloqueo con Face ID en la app.'],
];

export const Bienvenida: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white seguro-lados">
    {/* ── Cabecera ─────────────────────────────────────────────────────── */}
    <header className="sticky top-0 z-10 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur seguro-arriba">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
        <Logo size={30} />
        <span className="font-bold text-lg flex-1">{NOMBRE_APP}</span>
        <Link to="/login"
          className="px-3 py-2 rounded-full text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition">
          Entrar
        </Link>
        <span className="hidden sm:inline-flex"><BotonCrear /></span>
      </div>
    </header>

    <main>
      {/* ── Portada ─────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-16 sm:pt-16 grid gap-10 lg:grid-cols-2 items-center">
        <div className="text-center lg:text-left">
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight leading-tight">{LEMA_APP}</h1>
          <p className="mt-5 text-lg text-gray-600 dark:text-gray-400 max-w-xl mx-auto lg:mx-0">
            Tus cuentas, los gastos compartidos con amigos, las tareas de casa y las cosas que
            prestas, juntos en una app sencilla. Sin hojas de cálculo ni grupos de WhatsApp para echar cuentas.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
            <BotonCrear grande />
            <Link to="/login"
              className="inline-flex items-center justify-center px-7 py-3.5 rounded-full font-semibold
                         text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition">
              Ya tengo cuenta
            </Link>
          </div>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Entra con tu email, con Apple o con Google.
          </p>
          <div className="mt-6 flex justify-center lg:justify-start">
            <InsigniaAppStore />
          </div>
        </div>
        <Imagen archivo="portada.webp" alt={`${NOMBRE_APP} en el móvil y en el ordenador`}
          idea="Portada: la app en un iPhone (y quizá un portátil detrás), en un ambiente de hogar." />
      </section>

      {/* ── Un bloque por módulo, imagen y texto alternados ──────────────── */}
      {SECCIONES.map((s, i) => (
        <section key={s.etiqueta}
          className="max-w-6xl mx-auto px-4 py-12 sm:py-20 grid gap-8 lg:gap-16 lg:grid-cols-2 items-center">
          <div className={i % 2 ? 'lg:order-2' : ''}>
            <Imagen archivo={s.archivo} alt={s.alt} idea={s.idea} />
          </div>
          <div>
            <p className={`text-sm font-bold uppercase tracking-wider ${s.color}`}>{s.etiqueta}</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">{s.titulo}</h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">{s.texto}</p>
          </div>
        </section>
      ))}

      {/* ── Ventajas ────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 border-t border-gray-200 dark:border-gray-800 pt-12">
          {VENTAJAS.map(([titulo, texto]) => (
            <div key={titulo}>
              <p className="font-semibold text-lg">{titulo}</p>
              <p className="mt-1 text-gray-600 dark:text-gray-400">{texto}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Llamada final ───────────────────────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-4 py-16 sm:py-24 text-center">
        <Logo size={56} className="mx-auto" />
        <h2 className="mt-6 text-3xl sm:text-4xl font-bold tracking-tight">Empieza en un minuto</h2>
        <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">Crea tu cuenta y apunta tu primer gasto hoy mismo.</p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <BotonCrear grande />
          <InsigniaAppStore />
        </div>
      </section>
    </main>

    {/* ── Pie ──────────────────────────────────────────────────────────── */}
    <footer className="border-t border-gray-200 dark:border-gray-800 seguro-abajo">
      <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-2 flex-1"><Logo size={20} /> {NOMBRE_APP}</span>
        <nav className="flex gap-4">
          <Link to="/ayuda" className="hover:underline">Ayuda</Link>
          <Link to="/privacidad" className="hover:underline">Privacidad</Link>
          <Link to="/login" className="hover:underline">Entrar</Link>
        </nav>
      </div>
    </footer>
  </div>
);
